import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { BreadCrumb, SnackSimple } from '../../../../components'
import { CRUD, Status, Types } from '../../../../utils/enums'
import { InformativeCard } from '../../../../utils/types'
import { ObjectGeneric, onChangeEvent, Table } from '../../../../utils/types/generals'
import { initialData, initialValues, useMenuItems, input, formatFilterMainDivision } from './initialState'
import { getList, handleErrors, submit } from './methods'
import { useStyles } from './style'
import { statusService } from '../../../../service'
import { useContextState } from '../../../../hooks/theme'
import { divisionService } from '../../../../service/division'
import { divisionService as division } from "../../../../utils/constants/callService"
import { httpFetch } from '../../../../utils/crud'
import SimpleModal from '../../../../components/modal/simpleModal'
import { icons } from '../../../../utils/icons'
import { FiltersDrawer } from '../../../../components/filtersDrawer'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { DinamicForm } from '../../../../components/dinamicForm'
import { useFilter } from '../../../../hooks/filter'
import { returnQuery, returnQueryWithPagination } from '../../../../utils/methods/returnQuery'
import { ModelDownloadButtons } from '../../../../components/donwloadFiles/model'
import { useDownload } from '../../../../hooks/download/useDownload'
import { generalDisabled } from '../../../../utils/validate/validate'
import Form from './components/form'
import General from '../../../../components/modules/general'

const Division = () => {
  const { t } = useTranslation()
  const { colors } = useContextState()
  const styles = useStyles(colors)
  const [inputValues, setInputValues] = useState(initialValues)
  const [information, setInformation] = useState<InformativeCard | null>(null)
  const [data, setData] = useState<ObjectGeneric>({})
  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [allErrors, setAllErrors] = useState<any>({})
  const [openModalDelete, setOpenModalDelete] = useState(false)
  const inputs = input;
  const detailToInclude = ['description', 'cveDivision', 'createTime']
  const [open, setOpen] = useState(false)
  const [row, setRow] = useState<Table['row']>([])
  const [selected, setSelected] = useState<number>(-1)
  const [crud, setCrud] = useState(false)
  const [drawerDetail, setDrawerDetail] = useState({
    title: '',
    accept: '',
    cancel: '',
  })
  const refTime = useRef<NodeJS.Timeout | null>(null)
  const filterMain = useFilter({ formatFilter: formatFilterMainDivision })
  const { counter, errors, filterData, paginationModel, handlePagination, onChange, cleanFilter, sortTable, query, filterString } = filterMain
  const [totalPages, setTotalPages] = useState(0)

  const [openSnackbar, setOpenSnackbar] = useState({
    open: false,
    message: '',
    type: Types.SUCCESS,
  })
  const menuList = useMenuItems(styles)
  const [menu, setMenu] = useState<
    {
      label: string
      icon?: any
      value?: any
    }[]
  >(menuList)

  const handleCloseSnackbar = (value: boolean) => setOpenSnackbar({ ...openSnackbar, open: value })

  const crudChange = async (e: onChangeEvent | string): Promise<void> => {
    if (typeof e !== 'string') {
      const { name, value } = e?.target

      let body = {
        ...data,
        [name]: value?.value ?? value,
      }

      setData(body)
      setAllErrors(handleErrors(body, t))
    }
  }

  const handleSubmit = async () => {
    setBtnLoading(true)
    const response = await submit(data, t, information?.id)
    if (!response?.response?.errors) getAllDivisions()
    setOpenSnackbar(response.snackbarBody)
    setBtnLoading(false)
  }

  const handleInformation = (e: any, index: number) => {
    setSelected(index)
    openDrawer(true)
    let data: any[] = []
    setAllErrors({})
    if (e) {
      Object.entries(e ?? {}).forEach(([key, value]: any) => {
        if (!['status', 'id'].includes(key)) {
          data = [
            ...data,
            {
              label: t(`general.tags.${key}`),
              value: value?.label ?? value,
              selected: value?.value ?? value,
              name: key,
            },
          ]
        }
      })
      setMenu(menuList?.filter((item) => item.value !== e.status?.value))
    }

    setInformation(
      e
        ? {
          data,
          tmpData: data,
          status: {
            color: e.status.color,
            text: e.status.text,
            value: e.status?.value,
          },
          id: e.id,
        }
        : null
    )

    setData(initialData)
  }

  const handleMenu = (item: any) => {
    setBtnLoading(true)
    if (item.value === CRUD.EDIT) {
      setCrud(true)
      let body = {
        status: information?.status?.value,
      } as ObjectGeneric
      information?.tmpData?.forEach((element) => {
        body = {
          ...body,
          [element.name as string]: element?.selected ?? element.value,
        }
      })
      setData(body)
      setBtnLoading(false)
    } else if (item.value === CRUD.DELETE) {
      setOpenModalDelete(true)
    }
    setBtnLoading(false)
  }

  const resetValues = () => {
    setCrud(false)
    setOpen(false)
    setData(initialData)
    setBtnLoading(false)
    setSelected(-1)
  }
  const getAllDivisions = async () => {

    setIsLoading(true)
    const responseDivision = await getList(colors, t, returnQuery({ ...filterMain.query, filter: filterMain.query.filter?.replaceAll('+', '%2B') }))
    setTotalPages(responseDivision.totalPages);
    setRow(responseDivision.row)
    resetValues()
    setIsLoading(false)
    setData(initialData)
  }

  const handleDelete = () => {
    if (information?.id) {
      setBtnLoading(true)
      divisionService(httpFetch)
        .deleteDivision(information.id)
        .then((response) => {
          if (!response.errors) {
            getAllDivisions()
            setOpenSnackbar({
              open: true,
              type: Types.SUCCESS as string,
              message: t(`division.snackbar.delete.success`),
            })
          } else {
            setOpenSnackbar({
              open: true,
              type: Types.ERROR as string,
              message: t(`division.snackbar.delete.error`),
            })
          }
        })
        .catch(() => {
          setOpenSnackbar({
            open: true,
            type: Types.ERROR as string,
            message: t(`division.snackbar.delete.error`),
          })
        })
        .finally(() => {
          setBtnLoading(false)
          setOpenModalDelete(false)
        })
      return true
    }
  }

  const getData = async () => {
    getAllDivisions()
    const [responseStatus] = await Promise.all([statusService().statusList()])
    let valideStatus: any[] = []

    responseStatus?.forEach((item: any) => {
      if ([Status.ACTIVE, Status.INACTIVE].includes(item.id)) {
        valideStatus.push({
          value: item.id,
          label: item.description,
        })
      }
    })

    setInputValues({
      ...initialValues,
      status: {
        ...initialValues.status,
        items: valideStatus,
      },
    })
    // setStatusList(valideStatus)
  }


  const openDrawer = (edit?: boolean) => {
    setDrawerDetail({
      title: t(`division.informationCard.${edit ? 'edit' : 'create'}.title`),
      accept: t(`division.informationCard.buttons.${edit ? 'edit' : 'add'}`),
      cancel: t('userType.informationCard.buttons.cancel'),
    })
    if (!edit) {
      setData(initialData)
    }
    setInformation(null)
    setAllErrors({})
    setCrud(!edit)
    setOpen(true)
  }

  const closeDrawer = () => {
    setOpen(false)
    setCrud(false)
    setSelected(-1)
  }

  const downloadMain = useDownload({
    docName: t('division.filter.title'),
    query: returnQueryWithPagination({ filter: filterString, sort: query?.sort, }).replace('&', ''),
    downloadFuntion: division.downloadFile,
  })

  const column: any[] = [
    {
      id: 'description',
      label: t('general.tags.description'),
      sortable: true,
      minWidth: 100

    },
    {
      id: 'cveDivision',
      label: t('general.tags.cveDivision'),
      sortable: true,
      minWidth: 100

    },
    {
      id: 'createTime',
      label: t('general.tags.createTime'),
      sortable: true,
      minWidth: 100

    },
    {
      id: 'status',
      label: t('general.tags.status'),
      minWidth: 100

    },
  ]

  useEffect(() => {
    if (refTime.current) { clearTimeout(refTime.current) }

    refTime.current = setTimeout(() => {
      getData()
    }, 300)
  }, [filterMain.query.all])

  return (
    <>
      <General
        newHeader={<>
          <BreadCrumb
            removeBreadcrumb={['unit']}
            replaceLabels={{ division: 'storeDivision' }}
          />

          <Box sx={styles.header}>
            <Typography variant='title_grey_big'>{t('division.filter.title')}</Typography>

            <FiltersDrawer cleanFilter={cleanFilter} count={counter} right={-50} top={-40}>
              <Box sx={styles.section}>
                <DinamicForm errors={errors} input={Object.values(inputs)} values={filterData} onChange={onChange} filter={true} />
              </Box>
            </FiltersDrawer>

            <Box sx={styles.flex}>
              <ModelDownloadButtons
                csv={downloadMain.downloadCSV}
                pdf={downloadMain.downloadPDF}
                word={downloadMain.downloadWORD}
                disableCSV={downloadMain.download.excel}
                disablePDF={downloadMain.download.pdf}
                disableWORD={downloadMain.download.word}
                styleType
              />

              <Tooltip title={t('ToolTips.clearFilters')}>
                <IconButton
                  sx={(styles.clean, (counter || 0) > 0 ? null : styles.hide)}
                  onClick={() => {
                    cleanFilter()
                  }}
                >
                  {icons('clean')}
                </IconButton>
              </Tooltip>

              <Tooltip title={t('ToolTips.create')}>
                <IconButton
                  sx={styles.clean}
                  className='add'
                  onClick={() => {
                    openDrawer()
                  }}
                >
                  {icons('add')}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </>}
        open={open}
        rightDrawer={{
          ...information,
          data: information?.data?.filter((item) => detailToInclude.includes(item?.name ?? '')),
          form: <Form btnLoading={btnLoading} data={data} input={Object.values(inputValues)} crudChange={crudChange} allErrors={allErrors} />,
          menuItems: menu,
          crud,
          title: drawerDetail.title,
          accept: {
            title: drawerDetail.accept,
            disabled: generalDisabled(inputValues, data) || btnLoading,
            onClick: handleSubmit,
          },
          cancel: {
            title: drawerDetail.cancel,
            onClick: closeDrawer,
          },
          clickMenu: handleMenu,
        }}
        table={{
          column: column,
          row: row,
          title: t('division.filter.title'),
          loadingTable: isLoading,
          onClick: handleInformation,
          selected,
          serverSidePagination: {
            page: paginationModel.page + 1,
            size: paginationModel.pageSize,
            totalPages: totalPages,
          },
          handleChange: (page: number, pageSize: number) => {
            handlePagination({ page, pageSize })
          },
          sortable: (field, sort, count) => {
            sortTable(field && sort ? { field: field, sort: sort, count } : undefined)
          },
          order: query?.sort && {
            count: query?.sort?.count,
            key: query?.sort?.field,
            order: query?.sort?.sort,
          },
        }}
      />

      <SnackSimple close={handleCloseSnackbar} open={openSnackbar.open} type={openSnackbar.type} message={openSnackbar.message} />
      <SnackSimple
        close={downloadMain.closeOpenSnakbar}
        open={downloadMain.openSnackbar.open}
        type={downloadMain.openSnackbar.type as Types}
        message={downloadMain.openSnackbar.message}
      />
      <SimpleModal
        loadingButton
        isLoading={btnLoading}
        open={openModalDelete}
        close={() => {
          setOpenModalDelete(false)
        }}
        title={t('division.modal.delete.title')}
        description={''}
        buttonText={t('general.confirm')}
        cancelText={t('general.cancel')}
        color={colors}
        back={() => {
          setOpenModalDelete(false)
        }}
        next={handleDelete}
      />
    </>
  )
}

export default Division